import * as FullStory from '@fullstory/browser';
import { Box } from '@mantine/core';
import { useSuspenseGetBillingInfo } from '@repo/api-codegen/v2';
import { trackEvent } from '@repo/common/analytics';
import { isImpersonating } from '@repo/common/analytics/utils/isImpersonating';
import { useMount } from 'ahooks';
import { isEmpty } from 'lodash-es';
import { useLayoutEffect } from 'react';
import { Navigate, useLoaderData, useLocation } from 'react-router';
import { useAuthUser } from '../../api';
import { CreateEntityModalController } from '../../features/CreateEntityModal';
import { usePlan } from '../../hooks/usePlans';
import {
	useConfigureIntercom,
	useFullStory,
	useSentry,
} from '../../utils/analytics';
import { useV2CheckIAMPermission } from '../../utils/authorization/roles';

import { setGrafanaSessionMetadata } from '../../web-tracing';
import EntityDrawer from '../EntityDrawer/EntityDrawer';
import EntityModal from '../EntityModal/EntityModal';
import { sideBarStore } from '../SecodaAppShell/SideBar/store';
import { useSpotlightShortcuts } from '../Spotlight/useSpotlightShortcuts';
import BodyWrapper from './BodyWrapper';

import type { IPrivateRouteProps } from './types';

function PrivateRouteContent({
	children,
	hideSideBar,
	hideNavBar,
	adminRequired,
	editorRequired,
	guestRestricted,
	v2PermissionRequired,
}: IPrivateRouteProps) {
	const { user, workspace, isAdminUser, isEditorUser, isGuestUser } =
		useAuthUser();
	const { paywall } = usePlan();
	const location = useLocation();

	const { data: billingInfo } = useSuspenseGetBillingInfo({});

	useSpotlightShortcuts();
	useConfigureIntercom();
	useFullStory();
	useSentry();

	const v2PermissionCheckOk = useV2CheckIAMPermission({
		v2Permission: v2PermissionRequired,
		// Default to true because when the permission check is not done,
		// we don't want to redirect the user to the 403 page.
		defaultResult: true,
	});

	// track page views in Mixpanel
	useMount(() => {
		trackEvent(
			'page/view',
			{
				path: location.pathname,
			},
			user,
			workspace
		);

		if (FullStory.isInitialized()) {
			FullStory.setVars('page', {
				release: import.meta.env.VITE_FRONTEND_VERSION,
			});
		}
	});

	// eslint-disable-next-line no-use-effect/no-use-effect
	useLayoutEffect(() => {
		setGrafanaSessionMetadata({
			workspace_id: workspace?.id,
			user_id: user?.id,
			user_role: user?.role?.toString(),
		});
	}, [workspace, user]);

	// Handle subscription status-based redirects
	if (
		billingInfo?.subscription_status &&
		workspace.plan_name === 'Secoda Lite'
	) {
		const status = billingInfo.subscription_status;
		const allowedPaths = [
			'/onboarding/payment',
			'/onboarding/reactivate',
			'/onboarding/confirm',
			'/settings/billing',
			'/plans',
		];

		// Skip redirect if user is on an allowed onboarding/billing path
		if (allowedPaths.includes(location.pathname)) {
			// Continue to render the page
		} else if (['active', 'trialing'].includes(status)) {
			// Continue to render the page
		} else {
			return <Navigate to="/onboarding/reactivate" />;
		}
	}

	if (
		paywall &&
		location.pathname !== '/plans' &&
		location.pathname !== '/settings/billing' &&
		// Playwright/mailosaur users should not be redirected to the plans page
		// e.g. f50135ca@dloswa1m.mailosaur.net
		!user?.email.includes('playwright') &&
		!user?.email.includes('mailosaur')
	) {
		return <Navigate to="/plans" />;
	}

	if (workspace.migrated_permission_v2 && v2PermissionRequired) {
		if (!v2PermissionCheckOk) {
			return <Navigate to="/403" />;
		}
	} else {
		// Legacy permission check
		if (adminRequired && !isAdminUser) {
			return <Navigate to="/403" />;
		}
		if (editorRequired && !isEditorUser) {
			return <Navigate to="/403" />;
		}
		if (guestRestricted && isGuestUser) {
			return <Navigate to="/403" />;
		}
	}

	sideBarStore.setShowSideBar(!hideSideBar);

	return (
		<Box>
			<EntityDrawer />
			<EntityModal />
			<CreateEntityModalController />
			<BodyWrapper hideNavbar={hideNavBar}>{children}</BodyWrapper>
		</Box>
	);
}

export function PrivateRoute(props: IPrivateRouteProps) {
	// NOTE: This ensures workspace is loaded before rendering the page.
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const _ = useLoaderData();
	const { user, isNonAuthenticatedUser } = useAuthUser();

	// Authentication check
	if (isNonAuthenticatedUser) {
		return (
			<Navigate
				to={`/auth?final_destination=${encodeURI(window.location.href)}`}
			/>
		);
	}

	// If the user has not completed the onboarding flow, we redirect them to the profile page
	if (
		user &&
		(isEmpty(user?.first_name) || isEmpty(user?.last_name)) &&
		!isImpersonating()
	) {
		return <Navigate to="/onboarding/profile" />;
	}

	// If the user is not part of a workspace, we redirect them to the workspace creation flow
	if (user && isEmpty(user.workspace_id)) {
		return <Navigate to="/onboarding/workspace" />;
	}

	return <PrivateRouteContent {...props} />;
}

export default PrivateRoute;
